import React, { memo, useContext, useEffect } from 'react';
import StructuredData from './structuredData';
import Question from './question';
import Header from '../../atoms/Header';
import Container from '../../atoms/Container';
import {
  List,
  CustomFaqHeader,
  FaqImageContainer,
  FaqMoreQuestions,
} from './components';
import {
  eventParamAttributes,
  EventParamTypes,
  inViewEventAttributes,
} from '@spotify-internal/touchdown-analytics';
import { purify } from '../../utils/helpers';
import { trackEvent } from '../../../lib/tracking';
import { componentViewEvent } from '../../../lib/tracking/eventPayloadDefinitions';
import UserContext from '../../../configuration/context/user';
import MarketContext from '../../../configuration/context/market';
import { useInView } from 'react-intersection-observer';
import { getCookie } from '../../../lib/cookie-utils';

export type FaqPropTypes = {
  header?: React.ComponentProps<typeof Header>;
  container?: React.ComponentProps<typeof Container>;
  data?: {
    header: string;
    content: string;
  }[];
  eventParams?: EventParamTypes;
  text?: string;
};

export function TweakedFAQ(props: FaqPropTypes) {
  const { data, container, header, text, eventParams } = props;

  const { ref, inView } = useInView({
    fallbackInView: true,
    threshold: 0.6,
    triggerOnce: true,
  });

  const user = useContext(UserContext);
  const market = useContext(MarketContext);

  // When the component is inView, this code will create a componentViewEvent to track components impressions(event-definition: MarketingResourceViewEvent)
  useEffect(() => {
    if (inView === true) {
      const payload = {
        resource_name: 'premium',
        component_name: 'premium_tweak_faq_component',
        country: market?.country ?? 'unknown',
        locale: market?.language ?? 'unknown',
        full_url: window.location.href,
        correlation_id: getCookie('sp_t') ?? 'unknown',
        is_evergreen: market?.isEvergreen,
        eligibility: user?.eligibility,
      };

      trackEvent(
        componentViewEvent(payload, {
          authorize: user.authenticated,
        }),
      );
    }
  }, [market, user, inView]);

  return (
    <FaqImageContainer
      {...container}
      {...eventParamAttributes(eventParams)}
      {...inViewEventAttributes()}
    >
      <StructuredData data={data} />
      {header && <CustomFaqHeader {...header} />}
      {text && (
        <FaqMoreQuestions dangerouslySetInnerHTML={{ __html: purify(text) }} />
      )}
      <List ref={ref}>
        {data &&
          data.map((question, i) => (
            <Question key={`question-${i}`} {...question} />
          ))}
      </List>
    </FaqImageContainer>
  );
}

export default memo(TweakedFAQ);
